import axios from 'axios';
import { getBackendUrl } from './getBackendUrl';

const DB_NAME = 'dawal_offline';
const STORE = 'queue';
let syncing = false;

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const result = fn(store);
    t.oncomplete = () => resolve(result?.result !== undefined ? result.result : result);
    t.onerror = () => reject(t.error);
  });
};

const notifyChange = async () => {
  const count = await pendingCount();
  window.dispatchEvent(new CustomEvent('offline-queue-changed', { detail: { count } }));
};

export const pendingCount = async () => {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(0);
    });
  } catch {
    return 0;
  }
};

/** True when the error is a network failure (offline), not a server response. */
export const isNetworkError = (error) => !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || !navigator.onLine);

/**
 * Queue a request for later sync.
 * item: { url, method, jsonBody } OR { url, method, fileBlob, fileName, formFields } for multipart.
 * description: shown in the banner/toast.
 */
export const queueRequest = async (item) => {
  await tx('readwrite', (store) => store.add({ ...item, queued_at: new Date().toISOString() }));
  await notifyChange();
};

export const syncQueue = async () => {
  if (syncing || !navigator.onLine) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  syncing = true;
  window.dispatchEvent(new CustomEvent('offline-sync-status', { detail: { syncing: true } }));
  let synced = 0;
  let dropped = 0;
  try {
    const db = await openDb();
    const items = await new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
    for (const item of items) {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        let body = item.jsonBody;
        if (item.fileBlob) {
          body = new FormData();
          body.append('file', item.fileBlob, item.fileName || 'photo.jpg');
          Object.entries(item.formFields || {}).forEach(([k, v]) => body.append(k, v));
        }
        await axios({ url: item.url, method: item.method || 'POST', data: body, headers, timeout: 60000 });
        await tx('readwrite', (store) => store.delete(item.id));
        synced++;
      } catch (error) {
        if (isNetworkError(error)) {
          break; // still offline — stop, keep the rest queued
        }
        // Server rejected it (4xx/5xx) — drop so it doesn't block the queue forever
        await tx('readwrite', (store) => store.delete(item.id));
        dropped++;
        console.error('Offline sync: server rejected queued entry', item.url, error?.response?.status);
      }
    }
  } finally {
    syncing = false;
    await notifyChange();
    window.dispatchEvent(new CustomEvent('offline-sync-status', { detail: { syncing: false, synced, dropped } }));
  }
};

export const initOfflineSync = () => {
  window.addEventListener('online', () => setTimeout(syncQueue, 1500));
  setTimeout(syncQueue, 4000); // catch up shortly after app start
  setInterval(() => { pendingCount().then((c) => { if (c > 0) syncQueue(); }); }, 60000);
};

/** Helper for API base */
export const apiBase = () => `${getBackendUrl()}/api`;
